/**
 * Editorial motion layer for the storefront.
 *
 * - Scroll reveal: sections and grid items that start below the fold fade up once as
 *   they enter the viewport. Content that is already visible is never hidden, so
 *   first paint and LCP are unaffected, and markup stays visible without JavaScript.
 * - Pointer depth: with the "Editorial" card hover effect, product and collection card
 *   images drift slightly against the cursor. CSS owns the actual movement through the
 *   `--pointer-x` / `--pointer-y` custom properties set here.
 *
 * Both features are skipped for visitors who prefer reduced motion. Loaded once from
 * `snippets/scripts.liquid`, which also passes the merchant settings as data attributes.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const canHover = window.matchMedia('(hover: hover)');

const config = document.querySelector('script[data-motion-effects]');
const scrollRevealEnabled = config?.getAttribute('data-scroll-reveal') === 'true';

/* ---------------------------------- Scroll reveal ---------------------------------- */

/** Items revealed individually (with a stagger) instead of their whole section. */
const REVEAL_ITEM_SELECTOR = '.product-grid__item, .resource-list__item, .category-tile';

/**
 * Sections are identified by content because their wrapper IDs use template keys.
 * Left untouched: app-owned PagePilot sections, and purchase-critical sections (main
 * product, featured product, cart) whose fixed/sticky children must not sit inside a
 * transformed ancestor. Quick Add forms inside product cards don't count.
 * @param {HTMLElement} section
 * @returns {boolean}
 */
function shouldSkipSection(section) {
  if (section.classList.contains('pagepilot-section')) return true;
  if (section.querySelector('sticky-add-to-cart, cart-items-component')) return true;

  for (const form of section.querySelectorAll('product-form-component')) {
    if (!form.closest('.product-card, quick-add-component')) return true;
  }

  return false;
}

const MAX_STAGGER_STEPS = 6;
/** Longest reveal (duration + max stagger) plus a margin, used to clean up attributes. */
const REVEAL_CLEANUP_DELAY = 900 + MAX_STAGGER_STEPS * 70 + 150;

/** @type {IntersectionObserver | undefined} */
let revealObserver;

/**
 * Collects the elements to reveal: grid items when a section has a static grid, the
 * section itself otherwise. Items inside carousels reveal with their section, so slides
 * scrolled into view later never wait on an animation.
 * @returns {HTMLElement[]}
 */
function collectRevealTargets() {
  /** @type {HTMLElement[]} */
  const targets = [];
  const sections = document.querySelectorAll('main .shopify-section');

  for (const section of sections) {
    if (!(section instanceof HTMLElement) || shouldSkipSection(section)) continue;

    const items = [...section.querySelectorAll(REVEAL_ITEM_SELECTOR)].filter(
      (item) => item instanceof HTMLElement && !item.closest('slideshow-component')
    );

    if (items.length > 0) {
      targets.push(.../** @type {HTMLElement[]} */ (items));
    } else {
      targets.push(section);
    }
  }

  return targets;
}

/**
 * Removes reveal state once the transition has finished, so no transition or
 * `translate` lingers to interfere with hover effects or fixed descendants.
 * @param {HTMLElement} element
 */
function finishReveal(element) {
  delete element.dataset.reveal;
  element.style.removeProperty('--reveal-index');
}

/** @param {IntersectionObserverEntry[]} entries */
function handleRevealIntersections(entries) {
  let step = 0;

  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    const element = /** @type {HTMLElement} */ (entry.target);
    revealObserver?.unobserve(element);

    element.style.setProperty('--reveal-index', String(Math.min(step, MAX_STAGGER_STEPS)));
    element.dataset.reveal = 'shown';
    step += 1;

    setTimeout(() => finishReveal(element), REVEAL_CLEANUP_DELAY);
  }
}

function initScrollReveal() {
  if (!scrollRevealEnabled || reducedMotion.matches || !('IntersectionObserver' in window)) return;

  const targets = collectRevealTargets();
  if (targets.length === 0) return;

  // Read every position first, then write, to avoid interleaved layout work.
  const viewportHeight = window.innerHeight;
  const belowFold = targets.filter((target) => target.getBoundingClientRect().top >= viewportHeight);

  revealObserver = new IntersectionObserver(handleRevealIntersections, {
    rootMargin: '0px 0px -6% 0px',
  });

  for (const target of belowFold) {
    target.dataset.reveal = 'pending';
    revealObserver.observe(target);
  }

  // If the visitor switches on reduced motion mid-visit, show everything immediately.
  reducedMotion.addEventListener(
    'change',
    () => {
      revealObserver?.disconnect();
      for (const target of belowFold) finishReveal(target);
    },
    { once: true }
  );
}

/* ---------------------------------- Pointer depth ---------------------------------- */

const CARD_SELECTOR = '.product-card, .collection-card';

/** @type {HTMLElement | null} */
let activeCard = null;
/** @type {PointerEvent | null} */
let pendingPointer = null;
let frameRequested = false;

function applyPointerPosition() {
  frameRequested = false;
  if (!activeCard || !pendingPointer) return;

  const rect = activeCard.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  // Normalised to -0.5 … 0.5 from the card centre.
  const x = (pendingPointer.clientX - rect.left) / rect.width - 0.5;
  const y = (pendingPointer.clientY - rect.top) / rect.height - 0.5;

  activeCard.style.setProperty('--pointer-x', x.toFixed(3));
  activeCard.style.setProperty('--pointer-y', y.toFixed(3));
}

/** @param {HTMLElement} card */
function resetCard(card) {
  card.style.removeProperty('--pointer-x');
  card.style.removeProperty('--pointer-y');
}

/** @param {PointerEvent} event */
function handlePointerOver(event) {
  if (event.pointerType !== 'mouse' || reducedMotion.matches) return;
  if (!(event.target instanceof Element)) return;

  const card = event.target.closest(CARD_SELECTOR);
  if (!(card instanceof HTMLElement) || card === activeCard) return;

  if (activeCard) resetCard(activeCard);
  activeCard = card;
}

/** @param {PointerEvent} event */
function handlePointerMove(event) {
  if (!activeCard || event.pointerType !== 'mouse') return;

  pendingPointer = event;
  if (frameRequested) return;
  frameRequested = true;
  requestAnimationFrame(applyPointerPosition);
}

/** @param {PointerEvent} event */
function handlePointerOut(event) {
  if (!activeCard) return;
  if (event.relatedTarget instanceof Node && activeCard.contains(event.relatedTarget)) return;

  resetCard(activeCard);
  activeCard = null;
  pendingPointer = null;
}

function initPointerDepth() {
  if (!document.body.classList.contains('card-hover-effect-editorial')) return;
  if (!canHover.matches || reducedMotion.matches) return;

  document.addEventListener('pointerover', handlePointerOver, { passive: true });
  document.addEventListener('pointermove', handlePointerMove, { passive: true });
  document.addEventListener('pointerout', handlePointerOut, { passive: true });
}

initScrollReveal();
initPointerDepth();
